import type { SimulationCueSheetRow, SimulationPresentationKind } from '@/types/player/simulation-cue-sheet.types';
import type { LiveCueSheetV1, LiveCueSheetRowV1 } from '@/types/player/perform-control-v1.types';

function derivePresentationKind(row: LiveCueSheetRowV1): SimulationPresentationKind {
  if (row.isAgentBacked) {
    return 'agent';
  }
  const itemId = row.itemId ?? '';
  if (itemId.startsWith('composite:') || itemId.startsWith('virtualMedia:') || row.playAs === 'composite') {
    return 'composite';
  }
  return 'static';
}

/** Map live session CueSheet (perform-control WS) onto Control simulation cue rows. */
export function liveCueSheetToSimulationRows(cueSheet: LiveCueSheetV1): SimulationCueSheetRow[] {
  return (Array.isArray(cueSheet.items) ? cueSheet.items : [])
    .filter((row) => Boolean(row?.itemId) && !row?.flags?.detached)
    .map((row) => ({
      ordinal: row.ordinal,
      itemId: row.itemId,
      cueItemId: row.cueItemId,
      label: row.label,
      segmentAnchor: row.segmentAnchor,
      segmentKind: row.segmentKind,
      scope: row.scope,
      slotId: row.slotId,
      presentationKind: derivePresentationKind(row),
      advanceSettings: row.advanceSettings,
      fileType: row.fileType,
      fallbackCompositeItemId: row.fallbackCompositeItemId,
      isLastInScopePlaylist: row.isLastInScopePlaylist,
      operatorSkipped: Boolean(row.flags?.persistedSkip || row.flags?.skipped),
      nextShowMode: row.nextShowMode,
      // Resume lives in perform-control `mediaSequenceIndex` (seeded into the live slide store).
      // Do not map cue-row meta.slideIndex — rebuilds often preserve a stale per-row `1`.
    }));
}

export function liveCueSheetRevisionKey(cueSheet: LiveCueSheetV1): string {
  return `live:${cueSheet.revision}`;
}

export function resolveLiveCueRowSlotId(row: Pick<LiveCueSheetRowV1, 'slotId' | 'segmentAnchor'>): string | undefined {
  return row.slotId ?? (row.segmentAnchor?.startsWith('slot:') ? row.segmentAnchor.slice('slot:'.length) : undefined);
}

/** Playlist scope for a live cue row when `scope` is omitted (break playlists use media scope `slot`). */
export function resolveLiveCueRowPlaylistScope(
  row: Pick<LiveCueSheetRowV1, 'scope' | 'segmentKind'>,
): string | undefined {
  if (row.scope) {
    return row.scope;
  }
  if (row.segmentKind === 'break') {
    return 'slot';
  }
  return row.segmentKind || undefined;
}

/** Whether a live cue row belongs to the Control playlist currently rendering. */
export function liveCueRowMatchesControlPlaylist(
  row: Pick<LiveCueSheetRowV1, 'slotId' | 'scope' | 'segmentKind' | 'segmentAnchor'>,
  playlistScope?: string,
  playlistSlotId?: string,
): boolean {
  const slotId = resolveLiveCueRowSlotId(row);
  if (playlistSlotId) {
    if (!slotId || slotId !== playlistSlotId) {
      return false;
    }
  }
  if (!playlistScope) {
    return true;
  }
  const scope = resolveLiveCueRowPlaylistScope(row);
  return (
    scope === playlistScope ||
    row.segmentKind === playlistScope ||
    (playlistScope === 'slot' && (scope === 'slot' || row.segmentKind === 'break'))
  );
}
